// Data Lab v2 · Persons — LTV histogram + tier distribution + top-value roster.
// Wraps the same paginated LabListTable used by v1 with a rich top strip.

import { useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import api from "../../../lib/api";
import LabListTable from "../LabListTable";
import { UserRound, Trophy, TrendingUp, Wallet } from "lucide-react";
import { money, Chip, asText } from "../_shared";
import { KpiBand, KpiTile, Histogram, V2Banner, disableV2 } from "./_shared";
import { T2Panel } from "../../quotes/_t2";
import { Link } from "react-router-dom";

const TIER_TONE = (v) => {
  const s = String(v || "").toLowerCase();
  if (s.includes("platinum")) return "teal";
  if (s.includes("gold")) return "warn";
  if (s.includes("silver")) return "slate";
  if (s.includes("bronze")) return "orange";
  if (s.includes("black")) return "violet";
  return "slate";
};

const COLUMNS = [
  { key: "name",    label: "Full name",   path: "FullName",          sortable: true, w: "w-48", className: "font-semibold text-white" },
  { key: "email",   label: "Email",       path: "Email",             sortable: true, w: "w-64", className: "font-mono text-[#C8A46B] text-xs" },
  { key: "phone",   label: "Phone",       path: "Phone",             sortable: true, w: "w-36",
    fmt: (v) => v ? <span className="font-mono text-slate-200 text-xs">{v}</span> : <span className="text-slate-500">—</span> },
  { key: "tier",    label: "VIP Level",   path: "ShortVIPLevel",     sortable: true, w: "w-28",
    fmt: (v) => v ? <Chip tone={TIER_TONE(v)}>{asText(v)}</Chip> : "—" },
  { key: "company", label: "Company",     path: "Company",           sortable: true, w: "w-48" },
  { key: "orders",  label: "# Orders",    path: "NumberOfOrders",    sortable: true, w: "w-20", className: "font-mono text-white text-right" },
  { key: "ltv",     label: "LTV",         path: "TotalOrdersValue",  sortable: true, w: "w-28", fmt: money, className: "font-mono text-white font-bold" },
  { key: "avg",     label: "Avg order",   path: "AverageOrderValue", sortable: true, w: "w-28", fmt: money, className: "font-mono text-slate-300" },
];

const GBP0 = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

export default function LabPersonsV2() {
  const [insights, setInsights] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/lab/persons/insights"); setInsights(data); }
      catch { setInsights(null); }
    })();
  }, []);

  return (
    <Layout>
      <div data-testid="lab-persons-v2">
        <V2Banner onDisable={disableV2} />

        {/* Header */}
        <div style={{ marginBottom: 14 }}>
          <div className="t2-page-eyebrow">
            <UserRound className="w-3 h-3" /> Data Lab · Persons
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "var(--t2-white)", margin: "4px 0 3px" }}>Persons</h1>
          <p className="t2-mono" style={{ fontSize: 11.5, color: "var(--t2-grey3)" }}>
            Pipedrive-style records with lifetime-value distribution.
          </p>
        </div>

        {/* KPI band */}
        <KpiBand testid="persons-kpis">
          <KpiTile label="People"    value={insights?.total ?? "…"}                    icon={UserRound} testid="kpi-total" />
          <KpiTile label="With LTV"  value={insights?.with_ltv ?? "…"}                 icon={Wallet}    tone="teal"   testid="kpi-withltv" />
          <KpiTile label="Total LTV" value={GBP0(insights?.total_ltv)}                 icon={TrendingUp} tone="orange" testid="kpi-total-ltv" />
          <KpiTile label="Avg LTV"   value={GBP0(insights?.avg_ltv)} sub={`Max ${GBP0(insights?.max_ltv)}`} icon={TrendingUp} tone="brass" testid="kpi-avg-ltv" />
          <KpiTile label="Tiers"     value={insights?.tiers?.length ?? "…"}            icon={Trophy}    tone="violet" testid="kpi-tiers" />
        </KpiBand>

        {/* Histogram + Top LTV */}
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14, marginBottom: 14 }}>
          <T2Panel icon={TrendingUp} title="LTV distribution"
            right={<span className="t2-mono">{insights?.with_ltv ?? 0} people</span>}
            testid="persons-histogram-panel">
            {insights?.bins?.length ? (
              <Histogram bins={insights.bins.map((b) => ({ label: b.label, n: b.n }))} height={140} tone="orange" testid="persons-histogram" />
            ) : (
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>Loading…</div>
            )}
          </T2Panel>
          <T2Panel icon={Trophy} title="Top by LTV" testid="persons-top-ltv">
            {(insights?.top_ltv || []).length === 0 ? (
              <div className="t2-mono" style={{ fontSize: 11, color: "var(--t2-grey4)" }}>No LTV data.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {insights.top_ltv.map((p, i) => (
                  <Link key={p.id} to={`/lab/persons/${p.id}`}
                    style={{
                      display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center",
                      padding: "8px 10px", borderRadius: 8,
                      background: "var(--t2-surface2)", border: "1px solid var(--t2-border)",
                      textDecoration: "none",
                    }}>
                    <div className="t2-mono" style={{ color: "var(--t2-brass)", fontSize: 12, fontWeight: 800 }}>#{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "var(--t2-white)", fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.name || p.email || "—"}
                      </div>
                      <div className="t2-mono" style={{ fontSize: 10, color: "var(--t2-grey3)" }}>
                        {p.company || p.email || ""}
                        {p.tier ? <> · <span style={{ color: "var(--t2-teal)" }}>{p.tier}</span></> : null}
                      </div>
                    </div>
                    <div className="t2-mono" style={{ color: "var(--t2-orange)", fontWeight: 700, fontSize: 13 }}>{GBP0(p.ltv)}</div>
                  </Link>
                ))}
              </div>
            )}
          </T2Panel>
        </div>

        {/* Existing table (v1) reused for consistent behaviour */}
        <LabListTable
          collection="persons"
          columns={COLUMNS}
          defaultSort="TotalOrdersValue"
          defaultOrder="desc"
          detailPath={(id) => `/lab/persons/${id}`}
          searchPlaceholder="Search name, email, phone, company, postcode, city, VIP level…"
          testidPrefix="lab-person"
          alphaField="FullName"
        />
      </div>
    </Layout>
  );
}
